import {
  CardList,
  CardListShimmer,
  EntityList,
  Filters,
  matchFilters,
  useInMemoryFilteredList,
  EntityListNavigationType,
  SortDropdown,
  SortDirection,
  ListConductorRawFilter,
  OperandValue
} from '@hevoio/hevo-ui-toolkit';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activation } from '../../models/activation';
import { ActivationsFactory } from '../../utils';
import { elementHasSelection } from '../../../../core/utils/selection';
import { ActivateTour } from '../../ActivateTour/ActivateTour';
import ActivationsAPI from '../../api/ActivationsAPI';
import { ActivationListCardItem } from './ActivationListCardItem';
import { ACTIVATION_FILTERS, ACTIVATION_SORT_DROPDOWN_OPTIONS } from './constants';
import { CreateActivationButton } from './CreateActivationButton';

const SEARCH_FILTER_KEY = 'term';

export function ActivationList() {
  const navigate = useNavigate();

  const { fetch, reset } = useInMemoryFilteredList(
    async () => {
      const res = await ActivationsAPI.getActivations();
      return ActivationsFactory(res.data);
    },
    (item, params) => matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item)
      ),
    undefined,
    undefined,
    (item, field) => {
      if (field === 'id') {
        return item.id;
      }

      if (field === 'lastRunTs') {
        return item.lastRun?.transformationRunStats?.startedTs ? item.lastRun.transformationRunStats.startedTs : -1;
      }

      return item.name.trim().toLowerCase();
    },
    true
  );

  const getFilterKeyMatchValue = (filterKey: string, activation: Activation): OperandValue => {
    switch (filterKey) {
      case 'warehouse_name':
        return activation.warehouse.name;
      case 'target_name':
        return activation.target.name;
      case 'status':
        return activation.status.value;
      case SEARCH_FILTER_KEY:
        return [
          activation.seqId,
          activation.name,
          activation.warehouse.name,
          activation.target.name,
          activation.warehouse.typeDisplay,
          activation.target.targetTypeDisplay
        ];
      default:
        return undefined;
    }
  };

  return (
    <EntityList
      entity='activation'
      dataId='activate-list'
      classes={{ className: 'listing-card-flush-top' }}
      mode='standalone'
      navigationType={EntityListNavigationType.INFINITE_SCROLL}
      dataSource$={fetch}
      listIcon='activate'
      entityTitle=''
      emptyTitle='No Matching Activations Found'
      emptySubTitle='Modify search term to find the desired Activations'
      searchKey={SEARCH_FILTER_KEY}
      showSearch
      showFilters
      inMemoryList
      onRefresh={() => {
        reset();
      }}
      newListConnectedTitle='No Activation Created Yet'
      NewListComponent={ActivateTour}
      customNewListComponentProps={{
        onAddClick: () => {
          navigate('/activation/create');
        },
        hideCaption: true
      }}
      searchPlaceholder='Search Activations'
      genericErrorTitle='Error while fetching Activations'
      genericErrorSubTitle='Retry Loading the Activations'
      ListComponent={CardList}
      FiltersComponent={Filters}
      filtersComponentProps={{
        filters: ACTIVATION_FILTERS,
        entity: 'activation-list'
      }}
      listComponentProps={{
        CardListItem: ActivationListCardItem,
        onCardClick: (activation: Activation, e: any) => {
          if (e.disablePropagation || elementHasSelection(e.target)) {
            return;
          }

          navigate(`/activation/${activation.seqId}/overview`);
        }
      }}
      StandaloneModeActions={CreateActivationButton}
      disableFilters={false}
      pageLimit={50}
      withInfiniteScroll
      ShimmerComponent={CardListShimmer}
      SortComponent={SortDropdown}
      defaultListMeta={{ filters: [], sort: { id: SortDirection.DESC } }}
      sortComponentProps={{
        sortOptions: ACTIVATION_SORT_DROPDOWN_OPTIONS
      }}
    />
  );
}

